import { useContext, useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  SimpleGrid,
  HStack,
  Divider,
} from "@chakra-ui/react";
import CustomInput from "components/common/customInput/CustomInput";
import ShadowBox from "components/common/ShadowBox/ShadowBox";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

type FastForwardingVendorMaster = {
  _id?: string;
  vendorCode: string;
  vendorName: string;
  processName?: string;
};

const initialForm: FastForwardingVendorMaster = {
  vendorCode: "",
  vendorName: "",
  processName: "",
};

export default function FastForwardingVendorMasterComp(): JSX.Element {
  const { toastNotify } = useContext(AppContext);

  const [form, setForm] = useState<FastForwardingVendorMaster>(initialForm);
  const [list, setList] = useState<FastForwardingVendorMaster[]>([]);

  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (name: keyof FastForwardingVendorMaster, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const fetchVendorMasters = async () => {
    try {
      const res: any = await appRequest("fastForwardingVendorMaster", "getall", {});
      if (res?.status === "success") {
        setList(res?.data || []);
      }
    } catch (err: any) {
      toastNotify({
        title: "Fetch failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    }
  };

  useEffect(() => {
    fetchVendorMasters();
  }, []);

  const handleAdd = async () => {
    if (!form.vendorCode || !form.vendorName) {
      toastNotify({
        title: "Validation error",
        message: "Vendor Code and Vendor Name are required",
        type: "error",
      });
      return;
    }

    try {
      setLoading(true);

      if (editingId) {
        await appRequest("fastForwardingVendorMaster", "update", { id: editingId, ...form });
      } else {
        await appRequest("fastForwardingVendorMaster", "create", form);
      }

      toastNotify({
        title: "Success",
        message: editingId ? "Updated successfully" : "Saved successfully",
        type: "success",
      });

      setForm(initialForm);
      setEditingId(undefined);
      fetchVendorMasters();
      onClose();
    } catch (err: any) {
      toastNotify({
        title: "Operation failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    try {
      setLoading(true);
      await appRequest("fastForwardingVendorMaster", "delete", { id });

      toastNotify({
        title: "Deleted",
        message: "Vendor Master removed successfully",
        type: "success",
      });

      fetchVendorMasters();
    } catch (err: any) {
      toastNotify({
        title: "Delete failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const reader = new FileReader();
      reader.onload = async (event: any) => {
        const base64 = event.target.result;
        try {
          const res: any = await appRequest("fastForwardingVendorMaster", "excel/upload", {
            file: base64,
            processName: form.processName || "", // Use selected process if any
          });
          if (res?.status === "success") {
            toastNotify({
              title: "Upload Success",
              message: res.message || "Excel uploaded successfully",
              type: "success",
            });
            fetchVendorMasters();
          }
        } catch (error: any) {
          toastNotify({
            title: "Upload Failed",
            message: error.message || "Failed to upload Excel",
            type: "error",
          });
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <ShadowBox>
      <Flex mb={4} align="center" justify="space-between">
        <Box fontWeight="bold" fontSize="lg">
          Fast Forwarding Vendor Master
        </Box>

        <HStack>
          <input
            type="file"
            accept=".xlsx, .xls"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={handleFileUpload}
          />
          <Button
            colorScheme="blue"
            onClick={() => fileInputRef.current?.click()}
            isLoading={loading}
          >
            Upload Excel
          </Button>
          <Button
            colorScheme="teal"
            onClick={() => {
              setForm(initialForm);
              setEditingId(undefined);
              onOpen();
            }}
          >
            Add
          </Button>
        </HStack>
      </Flex>

      <Box mt={3} maxH="60vh" overflowY="auto">
        <Table size="sm" variant="striped">
          <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th>Vendor Code</Th>
              <Th>Vendor Name</Th>
              <Th>Process</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>

          <Tbody>
            {list.map((item) => (
              <Tr key={item._id}>
                <Td>{item.vendorCode}</Td>
                <Td>{item.vendorName}</Td>
                <Td>{item.processName || "-"}</Td>
                <Td>
                  <HStack>
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="blue"
                      onClick={() => {
                        setForm({ ...item });
                        setEditingId(item._id);
                        onOpen();
                      }}
                    >
                      Edit
                    </Button>

                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="red"
                      onClick={() => handleDelete(item._id)}
                    >
                      Delete
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* MODAL */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6px)" />
        <ModalContent borderRadius="2xl" boxShadow="2xl">
          <ModalHeader fontSize="lg" fontWeight="600">
            {editingId ? "Edit Vendor Master" : "Add Vendor Master"}
          </ModalHeader>

          <Divider />

          <ModalBody py={5}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <CustomInput name="vendorCode" label="Vendor Code" value={form.vendorCode} onChange={(e: any) => handleChange("vendorCode", e.target.value)} />
              <CustomInput name="vendorName" label="Vendor Name" value={form.vendorName} onChange={(e: any) => handleChange("vendorName", e.target.value)} />

              <Box>
                <Box fontSize="13px" fontWeight="500" mb={1}>
                  Process
                </Box>
                <select
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: "10px",
                    border: "1px solid #CBD5E0",
                  }}
                  value={form.processName}
                  onChange={(e) => handleChange("processName", e.target.value)}
                >
                  <option value="">Select Process</option>
                  <option value="domestic">Domestic</option>
                  <option value="marcan">Marcan</option>
                  <option value="tillomed">Tillomed</option>
                  <option value="emerging">Emerging</option>
                  <option value="zuventus">Zuventus</option>
                  <option value="foreign">Foreign</option>
                  <option value="provakil">Provakil</option>
                </select>
              </Box>
            </SimpleGrid>
          </ModalBody>

          <Divider />

          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={handleAdd} isLoading={loading}>
              {editingId ? "Update" : "Save"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </ShadowBox>
  );
}
